import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SourceRow } from "@/components/citations";
import { Button } from "@/components/ui/button";
import {
  citationSourceForLegacy,
  citationSourceForTarget,
} from "../citationSources";
import { webCitationSource } from "../ThreadWebSourcesList";

import { CitationSource } from "@/components/citations";
import { CitationSourceContext } from "../citationSources";
import { ThreadSourceEntry } from "../threadSources";

const DEFAULT_LIMIT = 10;

interface ThreadSourcesSectionProps {
  entries: Array<ThreadSourceEntry>;
  ctx: CitationSourceContext;
  /** Rows shown before "Show all". */
  limit?: number;
}

/**
 * A thread-wide row: icon and name only. The location stays where it names
 * the source itself (web host and date, connector app); a page, finding or
 * speaker belongs to one citation, not to the source. No quote, preview,
 * figures or thumbnail, so nothing loads.
 */
function compactSource(entry: ThreadSourceEntry, ctx: CitationSourceContext): CitationSource {
  const { representative } = entry;
  const source =
    representative.kind === "target"
      ? citationSourceForTarget(representative.target, representative.index, ctx)
      : representative.kind === "legacy"
        ? citationSourceForLegacy(representative.citation, representative.index, ctx)
        : webCitationSource(representative.citation, representative.index);
  const keepsLocation = source.kind === "web" || source.kind === "connector_record";
  return {
    ...source,
    key: entry.key,
    location: keepsLocation ? source.location : null,
    quote: null,
    preview: null,
    figures: null,
    image: null,
  };
}

/** The side pane's Sources: every source the thread cited, the most cited first. */
export function ThreadSourcesSection(props: ThreadSourcesSectionProps) {
  const { entries, ctx, limit = DEFAULT_LIMIT } = props;

  const [isShowingAll, setIsShowingAll] = useState(false);

  const { t } = useTranslation("app");

  const visible = isShowingAll ? entries : entries.slice(0, limit);

  const showAll =
    !isShowingAll && entries.length > limit ? (
      <Button
        variant="ghost"
        size="sm"
        className="self-start text-xs text-muted-foreground"
        data-testid="thread-session-sources-show-all"
        onClick={() => setIsShowingAll(true)}
      >
        {t("threads.sessions.showAllSources", { count: entries.length })}
      </Button>
    ) : null;

  return (
    <>
      <ul className="flex flex-col" data-testid="thread-session-sources-list">
        {visible.map((entry, index) => {
          const cited = t("threads.sessions.citedCount", { count: entry.count });
          return (
            <li key={entry.key} className="flex items-start gap-1">
              <div className="min-w-0 flex-1">
                <SourceRow
                  source={compactSource(entry, ctx)}
                  testId={`thread-session-source-${index}`}
                />
              </div>
              <span
                className="mt-1.5 shrink-0 pr-1 text-xs tabular-nums text-muted-foreground"
                data-testid={`thread-session-source-count-${index}`}
                title={cited}
                aria-label={cited}
              >
                {t("threads.sessions.citedCompact", { count: entry.count })}
              </span>
            </li>
          );
        })}
      </ul>
      {showAll}
    </>
  );
}
